<template>
  <div class="search-game-page">
    <van-nav-bar
      :title="$lang.search_title"
      :border="false"
      fixed
      z-index="99999"
      @click-left="onClickLeft"
    >
      <template #left>
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </template>
    </van-nav-bar>

    <div class="search-game-content">
      <div class="search-input-wrap">
        <van-field
          v-model="searchKeyword"
          :placeholder="$lang.search_placeholder"
          class="search-field"
          @keyup.enter="onSearch"
        >
          <template #left-icon>
            <img
              src="../../../assets/img/common/ssk_com.png"
              width="18"
              class="search-icon"
            />
          </template>
        </van-field>
      </div>

      <!-- 搜索历史 -->
      <div v-if="searchHistory.length > 0" class="section">
        <h3 class="section-title">
          {{ $lang.search_history }}
          <span class="clear-all" @click="clearAllHistory">{{
            $lang.search_clear
          }}</span>
        </h3>
        <div class="tag-list">
          <span
            v-for="(item, idx) in searchHistory"
            :key="'h-' + idx"
            class="tag"
            @click="onHistoryClick(item)"
          >
            {{ item }}
            <van-icon
              name="cross"
              size="12"
              class="tag-close"
              @click.stop="removeHistory(idx)"
            />
          </span>
        </div>
      </div>

      <!-- 热门搜索：有搜索内容时隐藏，清空后展示 -->
      <div
        v-if="popularNames.length > 0 && !hasSearchKeyword"
        class="section"
      >
        <h3 class="section-title">{{ $lang.search_popular }}</h3>
        <div class="tag-list">
          <span
            v-for="(name, idx) in popularNames"
            :key="'p-' + idx"
            class="tag"
            @click="onPopularClick(name)"
          >
            {{ name }}
          </span>
        </div>
      </div>

      <!-- 推荐游戏：有搜索内容时隐藏，清空后展示 -->
      <div
        v-if="recommendedGames.length > 0 && !hasSearchKeyword"
        class="section"
      >
        <h3 class="section-title">{{ $lang.search_recommended }}</h3>
        <div class="game-grid">
          <div
            v-for="(game, idx) in recommendedGames"
            :key="'r-' + idx"
            class="game-item"
            @click="goToGame(game.gid, game)"
          >
            <van-image
              :src="game.gameIcon"
              width="100%"
              radius="10"
              lazy-load
              class="game-cover"
            >
              <template v-slot:loading>
                <van-loading size="20" color="#ff5a1f" vertical />
              </template>
            </van-image>
            <!-- <p class="game-name">{{ game.gameName }}</p> -->
          </div>
        </div>
      </div>

      <van-empty
        v-if="
          !loading &&
          !readError &&
          ((hasSearchKeyword && searchResult.length === 0) ||
            (!hasSearchKeyword && allGames.length === 0))
        "
        :description="$lang.common_txt145"
        class="empty-tip"
      />
      <div v-if="readError" class="read-error-tip">{{ readError }}</div>
      <div v-if="hasSearchKeyword && searchResult.length > 0" class="section">
        <div class="game-grid">
          <div
            v-for="(game, idx) in searchResult"
            :key="'s-' + idx"
            class="game-item"
            @click="goToGame(game.gid, game)"
          >
            <van-image
              :src="game.gameIcon"
              width="100%"
              radius="10"
              lazy-load
              class="game-cover"
            >
              <template v-slot:loading>
                <van-loading size="20" color="#ff5a1f" vertical />
              </template>
            </van-image>
            <!-- <p class="game-name">{{ game.gameName }}</p> -->
          </div>
        </div>
      </div>
    </div>

    <login v-model="showPopup" overlay></login>
  </div>
</template>

<script>
import { GetHomeGameList } from '@/api/common'
import {
  buildLaunchPayload,
  collectGamesFromGroups,
  getVisibleGameGroups,
  launchGame
} from '@/components/home/gameLaunch'
import {
  filterDisplayGames,
  gameResponseBody,
  gameUnavailableMessage,
  isHomeGameReadableResponse,
  isUnavailableGameEntry
} from './gameResponse'
import {
  HISTORY_MAX,
  nextSearchHistory,
  safeReadSearchHistory,
  safeSaveSearchHistory
} from './searchHistory'
import { safeLocalStorage } from '@/utils/safeStorage'
import { h5Text } from '@/utils/h5I18n'

const POPULAR_COUNT = 6
const RECOMMENDED_COUNT = 3
function getSearchStorage() {
  return safeLocalStorage()
}

const HOME_GAME_MARKER_ALIAS_KEYS = [
  'readOnlyMode',
  'read_only_mode',
  'mockMode',
  'mock_mode',
  'falseProof',
  'false_proof',
  'failClosed',
  'fail_closed',
  'placeholder',
  'readUnavailable',
  'read_unavailable',
  'routeUnavailable',
  'route_unavailable',
  'technicalMarker',
  'technical_marker',
  'technicalError',
  'technical_error',
  'technicalException',
  'technical_exception',
  'backendFailure',
  'backend_failure',
  'notConnected',
  'not_connected'
]

function homeGameAliasToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function isActiveHomeGameMarkerAlias(value) {
  const token = homeGameAliasToken(value)
  return value === true ||
    value === 1 ||
    ['true', '1', 'yes', 'on'].includes(token) ||
    token.includes('read') ||
    token.includes('mock') ||
    token.includes('placeholder') ||
    token.includes('fallback') ||
    token.includes('fail_closed') ||
    token.includes('fail-closed') ||
    token.includes('fail closed') ||
    token.includes('failclosed') ||
    token.includes('technicalmarker') ||
    token.includes('technical_marker') ||
    token.includes('backendfailure') ||
    token.includes('backend_failure') ||
    token.includes('未接入') ||
    token.includes('只读')
}

function hasHomeGameMarkerAlias(entry) {
  const item = entry && typeof entry === 'object' && !Array.isArray(entry) ? entry : {}
  return HOME_GAME_MARKER_ALIAS_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(item, key) && isActiveHomeGameMarkerAlias(item[key])
  )
}

function isSafeHomeGameEntry(game) {
  return !!(game && typeof game === 'object' && !isUnavailableGameEntry(game) && !hasHomeGameMarkerAlias(game))
}

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export default {
  name: 'SearchGame',
  data() {
    return {
      searchKeyword: '',
      pgDate: {},
      allGames: [],
      popularNames: [],
      recommendedGames: [],
      searchHistory: [],
      loading: false,
      readError: '',
      showPopup: false
    }
  },
  computed: {
    hasSearchKeyword() {
      return this.searchKeyword.trim().length > 0
    },
    searchResult() {
      if (!this.hasSearchKeyword) return []
      const kw = this.searchKeyword.toLowerCase().trim()
      return this.allGames.filter((g) =>
        (g.gameName || '').toLowerCase().includes(kw)
      )
    }
  },
  created() {
    this.loadSearchHistory()
  },
  mounted() {
    this.GetHomeGameList()
  },
  methods: {
    loadSearchHistory() {
      this.searchHistory = safeReadSearchHistory(getSearchStorage(), HISTORY_MAX)
    },
    saveSearchHistory(name) {
      if (!name || !name.trim()) return
      this.searchHistory = nextSearchHistory(this.searchHistory, name, HISTORY_MAX)
      safeSaveSearchHistory(getSearchStorage(), this.searchHistory, HISTORY_MAX)
    },
    removeHistory(index) {
      this.searchHistory.splice(index, 1)
      safeSaveSearchHistory(getSearchStorage(), this.searchHistory, HISTORY_MAX)
    },
    clearAllHistory() {
      this.searchHistory = []
      safeSaveSearchHistory(getSearchStorage(), this.searchHistory, HISTORY_MAX)
    },
    buildAllGames() {
      const groups = getVisibleGameGroups(this.pgDate, this.getCurrency)
      return filterDisplayGames(collectGamesFromGroups(groups, { dedupe: true }))
        .filter((game) => isSafeHomeGameEntry(game))
    },
    pickRandomNames(games, count) {
      const names = [...new Set(games.map((g) => g.gameName).filter(Boolean))]
      return shuffle(names).slice(0, count)
    },
    pickRandomGames(games, count) {
      return shuffle(games).slice(0, count)
    },
    clearGameList() {
      this.pgDate = {}
      this.allGames = []
      this.popularNames = []
      this.recommendedGames = []
    },
    showReadFailure(message) {
      const fallback = this.$lang.home_game_unavailable || h5Text('home_game_unavailable') || 'Game list is temporarily unavailable.'
      const safeMessage = String(message == null ? '' : message).trim()
      this.readError = gameUnavailableMessage(/[\u4e00-\u9fff]/.test(safeMessage) ? fallback : safeMessage || fallback)
      this.clearGameList()
      this.$toast({
        message: this.readError,
        icon: 'cross'
      })
    },
    async GetHomeGameList() {
      this.loading = true
      try {
        const data = await GetHomeGameList()
        if (isHomeGameReadableResponse(data)) {
          this.readError = ''
          this.pgDate = gameResponseBody(data)
          this.allGames = this.buildAllGames()
          this.popularNames = this.pickRandomNames(
            this.allGames,
            POPULAR_COUNT
          )
          this.recommendedGames = this.pickRandomGames(
            this.allGames,
            RECOMMENDED_COUNT
          )
        } else {
          this.showReadFailure()
        }
      } catch (_) {
        this.showReadFailure()
      } finally {
        this.loading = false
      }
    },
    onClickLeft() {
      this.$jumpTo('/home', {}, { replace: true })
    },
    onSearch() {
      const kw = this.searchKeyword.trim()
      if (kw) this.saveSearchHistory(kw)
    },
    onHistoryClick(name) {
      this.searchKeyword = name
    },
    onPopularClick(name) {
      this.saveSearchHistory(name)
      this.searchKeyword = name
    },
    goToGame(v, v2) {
      if (this.readError) {
        this.$toast({
          message: this.readError,
          icon: 'cross'
        })
        return
      }
      if (this.token) {
        const game = v2 || (v && typeof v === 'object' ? v : null)
        if (!game || isUnavailableGameEntry(game) || !isSafeHomeGameEntry(game)) {
          this.$toast({
            message: gameUnavailableMessage(this.$lang.common_txt160),
            icon: 'cross'
          })
          return
        }
        const jumpPayload = buildLaunchPayload(game, {
          isDemo: this.$route.query.isDemo || this.$route.query.demo,
          typeCate: 1,
          gameTab: this.$route.query.gameTab,
          typeCode: game && (game.typeCode || game.code)
        })
        if (!jumpPayload) {
          this.$toast({
            message: gameUnavailableMessage(this.$lang.common_txt160),
            icon: 'cross'
          })
          return
        }
        launchGame(this, jumpPayload)
      } else {
        this.showPopup = true
      }
    }
  }
}
</script>

<style lang="less" scoped>
.search-game-page {
  min-height: 100vh;
  padding-top: 55px;
  background: #141622;
}
.search-game-content {
  padding: 12px 12px 60px;
}
.search-input-wrap {
  margin-bottom: 20px;
}
.search-field {
  background: #2d3242;
  border-radius: 10px;
  border: none;
  padding: 10px 12px;
  ::v-deep .van-field__control {
    color: #ffffff;
  }
  ::v-deep .van-field__left-icon {
    padding-right: 8px;
  }
}
.search-icon {
  opacity: 0.9;
}
.section {
  margin-bottom: 24px;
}
.section-title {
  font-size: var(--vk-fs-15);
  color: #ffffff;
  margin: 0 0 12px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .clear-all {
    font-size: var(--vk-fs-xs);
    color: #9da7bc;
    cursor: pointer;
  }
}
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tag {
  background: #363849;
  color: #ffffff;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: var(--vk-fs-sm);
  cursor: pointer;
  position: relative;
  display: inline-flex;
  align-items: center;
  .tag-close {
    margin-left: 6px;
    opacity: 0.7;
    &:hover {
      opacity: 1;
    }
  }
}
.game-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.game-item {
  width: 31.5%;
  cursor: pointer;
  max-height: 160px !important;
  position: relative;
}
.game-cover {
  display: block;
  overflow: hidden;
  ::v-deep .van-image__img {
    border-radius: 13px;
    max-height: 160px !important;
    height: 145px !important;
    object-fit: cover;
  }
}
.game-name {
  position: absolute;
  bottom: 5px;
  left: 0;
  right: 0;
  margin: 0;
  font-size: var(--vk-fs-2xs);
  color: #ffffff;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 2px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
}
.empty-tip {
  padding: 40px 0;
}
.read-error-tip {
  margin: 60px 8px 0;
  padding: 12px 14px;
  border: 1px solid rgba(255, 96, 96, 0.35);
  border-radius: 8px;
  background: rgba(255, 96, 96, 0.1);
  color: #ffb4b4;
  font-size: var(--vk-fs-sm);
  line-height: 1.5;
}
</style>
